import type { CustomField, NameMode, RegistrationField } from "./types";

export type FieldMessageKey = string;

/** Message and form-state key for a field. Custom fields are keyed by definition id because
 *  several can share a name. */
export function fieldKeyOf(field: RegistrationField): FieldMessageKey {
  return field.key === "custom" ? `custom:${field.id}` : field.key;
}

/** Rebuilds the ordered list from the older payload shape, so a bundle that reloads before the
 *  API deploys still renders the fields it used to. */
export function fallbackRegistrationFields(
  nameMode: NameMode,
  customFields: CustomField[]
): RegistrationField[] {
  const fields: RegistrationField[] = [];

  const nameField = (
    id: number,
    key: RegistrationField["key"],
    name: string
  ): RegistrationField => ({
    id,
    key,
    name,
    type: "Text",
    htmlInputType: "text",
    required: true
  });

  if (nameMode === "single") fields.push(nameField(2, "name", "Name"));
  if (nameMode === "split") {
    fields.push(nameField(8, "firstName", "First name"));
    fields.push(nameField(9, "lastName", "Last name"));
  }

  fields.push({
    id: 0,
    key: "email",
    name: "Email",
    type: "Text",
    htmlInputType: "text",
    required: false
  });
  fields.push({
    id: 1,
    key: "phone",
    name: "Phone",
    type: "Text",
    htmlInputType: "text",
    required: false
  });

  for (const field of customFields) {
    fields.push({ ...field, key: "custom" });
  }

  return fields;
}

export function resolveRegistrationFields(
  registrationFields: RegistrationField[] | null | undefined,
  nameMode: NameMode,
  customFields: CustomField[]
): RegistrationField[] {
  if (registrationFields && registrationFields.length > 0)
    return registrationFields;
  return fallbackRegistrationFields(nameMode, customFields);
}

export type FieldRow =
  | { kind: "pair"; fields: [RegistrationField, RegistrationField] }
  | { kind: "single"; field: RegistrationField };

/** Keeps First name / Last name side by side when the admin ordered them together. */
export function groupRegistrationFields(
  fields: RegistrationField[]
): FieldRow[] {
  const rows: FieldRow[] = [];

  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];
    const next = fields[i + 1];

    if (field.key === "firstName" && next?.key === "lastName") {
      rows.push({ kind: "pair", fields: [field, next] });
      i++;
      continue;
    }

    rows.push({ kind: "single", field });
  }

  return rows;
}
